import { getAssetUrl } from '@/lib/utils/assets';

/**
 * Explicit CDN variant URLs for the onboarding slide screenshots (#410).
 * Each slide has a mobile (414w) and desktop (800w) WebP, served from R2+CDN
 * (assets.til.sg / stg-assets.til.sg) with immutable cache headers. URLs are
 * explicit (not inferred from filenames) so a rename can't silently break the
 * slide srcset. These are preloaded on the login route so first-login
 * onboarding renders with no visible image loads.
 *
 * Desktop is capped at the source screenshot's native 800px width — the
 * originals are 800×1312, so any wider variant would just upscale and overserve.
 */
export const ONBOARDING_IMAGE_SOURCES = [1, 2, 3].map((n) => ({
  imageMobile: getAssetUrl(`/onboarding/screen-${n}-mobile.webp`),
  imageDesktop: getAssetUrl(`/onboarding/screen-${n}-desktop.webp`),
}));

/** Same-origin Lottie asset for the "Start dancing" celebration. */
export const CELEBRATION_LOTTIE_PATH = '/onboarding/celebration-confetti.lottie';

/**
 * The exact srcset/sizes the slide hero img renders (onboarding-slide.tsx).
 * Both the img and the login preload hint must be built from these so the
 * browser preloads precisely the variant it later paints; a mismatch would
 * leave the preload unused (console "preload not used" warning) or double-fetch.
 */
export const ONBOARDING_HERO_SIZES = '(max-width: 767px) 90vw, 720px';
export const onboardingHeroSrcSet = (v: { imageMobile: string; imageDesktop: string }): string =>
  `${v.imageMobile} 414w, ${v.imageDesktop} 800w`;

/**
 * Route-scoped preload hints for the login entry: warms the first onboarding
 * slide image (low priority, both breakpoints), prefetches the remaining two
 * slides, and preloads the celebration .lottie — all while the learner is still
 * on the login screen, so first-login onboarding renders with no visible image
 * loads (#410). Co-located with the asset URLs so there is a single source of
 * truth for what gets preloaded and the login route body stays lean.
 */
export const getOnboardingPreloadLinks = () => {
  const [first, ...rest] = ONBOARDING_IMAGE_SOURCES;
  // The first slide is preloaded with the SAME srcset+sizes the hero img
  // renders (supersedes no-mistakes #982's ungated two-preload hack): the
  // browser then selects exactly the variant it would paint (density-aware,
  // e.g. a 393px @3x phone picks the 800w desktop variant), so the preload is
  // always consumed and never logs "preloaded but not used". It runs at LOW
  // priority: the login backdrop hero owns the fetchpriority='high' lane and
  // the slide is only needed after a successful login, so a cold login load
  // must never have the two high-priority preloads fight for bandwidth. The
  // remaining slides are low-priority prefetches gated by breakpoint
  // (best-effort), and the celebration .lottie is a prefetch too: it is only
  // consumed at onboarding completion, so a preload would sit unused for the
  // whole login screen and warn on every load. crossOrigin stays on the
  // .lottie so the cached response matches the cors-mode fetch() that
  // consumes it.
  return [
    {
      rel: 'preload',
      as: 'image',
      imageSrcSet: onboardingHeroSrcSet(first!),
      imageSizes: ONBOARDING_HERO_SIZES,
      fetchPriority: 'low' as const,
    },
    ...rest.flatMap((source) => [
      { rel: 'prefetch', as: 'image', href: source!.imageMobile, media: '(max-width: 767px)' },
      { rel: 'prefetch', as: 'image', href: source!.imageDesktop, media: '(min-width: 768px)' },
    ]),
    {
      rel: 'prefetch',
      as: 'fetch',
      href: CELEBRATION_LOTTIE_PATH,
      crossOrigin: 'anonymous' as const,
    },
  ];
};
